import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { cleaningApi } from '../lib/cleaningApi';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, Download, Printer, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function CleaningInvoiceDetail() {
  const { invoiceId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        // We need to add getInvoice to cleaningApi or call fetch directly
        // Assuming cleaningApi doesn't have it yet, we'll use fetch with token
        const token = localStorage.getItem('cleaning_admin_token');
        if (!token) throw new Error('Not authenticated');

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cleaning/invoices/${invoiceId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          if (response.status === 404) throw new Error('Invoice not found');
          throw new Error('Failed to load invoice');
        }

        const data = await response.json();
        setBooking(data);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (invoiceId) {
      fetchInvoice();
    }
  }, [invoiceId]);

  const handleDownloadPdf = async () => {
    if (!booking) return;
    try {
      const token = localStorage.getItem('cleaning_admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cleaning/admin/bookings/${booking.id}/receipt`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to download PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PlatinumTouch_Invoice_${booking.invoice_id || booking.id.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <Card className="w-full max-w-md text-center p-6">
          <div className="text-red-500 mb-4 text-xl font-bold">Error</div>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button asChild>
            <Link to="/cleaning/admin/dashboard">Back to Dashboard</Link>
          </Button>
        </Card>
      </div>
    );
  }

  if (!booking) return null;

  const isPaid = booking.payment_status === 'paid';
  const subtotal = booking.price;
  const total = booking.total_amount || booking.price * 1.06625; // Estimate tax if not present
  const tax = total - subtotal;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8 print:bg-white print:p-0">
      <div className="max-w-4xl mx-auto">
        {/* Header Actions */}
        <div className="flex items-center justify-between mb-6 print:hidden">
          <Button variant="outline" asChild>
            <Link to="/cleaning/admin/dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
            </Link>
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button onClick={handleDownloadPdf} className="bg-emerald-500 hover:bg-emerald-600 text-white">
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
          </div>
        </div>

        {/* Invoice Card */}
        <Card className="bg-white shadow-lg overflow-hidden print:shadow-none print:border-none">
          {/* Status Banner */}
          <div className={`h-2 w-full ${isPaid ? 'bg-emerald-500' : 'bg-amber-500'}`} />
          
          <div className="p-8 sm:p-12">
            {/* Invoice Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start mb-12">
              <div>
                <img src="/pt-logo.png?v=2" alt="Platinum Touch" className="h-16 w-auto mb-4" />
                <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">PLATINUM TOUCH</h1>
                <p className="text-emerald-500 font-medium tracking-widest text-sm uppercase mt-1">Car Cleaning & Detailing</p>
              </div>
              <div className="mt-6 sm:mt-0 text-right">
                <h2 className={`text-4xl font-bold ${isPaid ? 'text-emerald-500' : 'text-amber-500'}`}>
                  {isPaid ? 'PAID' : 'INVOICE'}
                </h2>
                <p className="text-slate-500 mt-2 font-mono">#{booking.invoice_id || 'PENDING'}</p>
                <p className="text-slate-500 text-sm mt-1">
                  Issued: {new Date(booking.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Bill To / Service Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div>
                <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-4">Bill To</h3>
                <p className="font-bold text-slate-900 text-lg">{booking.customer.full_name}</p>
                <p className="text-slate-600">{booking.customer.email}</p>
                <p className="text-slate-600">{booking.customer.phone}</p>
                {booking.service_address && (
                  <p className="text-slate-600 mt-2">
                    {booking.service_address.street}<br />
                    {booking.service_address.city}, {booking.service_address.state} {booking.service_address.zip_code}
                  </p>
                )}
              </div>
              <div className="md:text-right">
                <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-4">Service Details</h3>
                <p className="font-medium text-slate-900">{booking.service_name}</p>
                <p className="text-slate-600">Date: {new Date(booking.preferred_date).toLocaleDateString()}</p>
                <p className="text-slate-600">Time: {booking.preferred_time}</p>
                <p className="text-slate-600 mt-2">
                  Booking ID: <span className="font-mono text-xs">{booking.id}</span>
                </p>
              </div>
            </div>

            {/* Line Items */}
            <div className="mb-12">
              <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-4">Description</h3>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-xs font-medium text-slate-500 uppercase">Item</th>
                      <th className="px-6 py-3 text-xs font-medium text-slate-500 uppercase text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <tr>
                      <td className="px-6 py-4">
                        <p className="font-medium text-slate-900">{booking.service_name} - {booking.vehicle_size_label}</p>
                        <p className="text-sm text-slate-500 mt-1">
                          {booking.vehicle.year} {booking.vehicle.make} {booking.vehicle.model} ({booking.vehicle.color})
                        </p>
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-slate-900">
                        ${booking.price.toFixed(2)}
                      </td>
                    </tr>
                    {/* Additional items if any */}
                    {booking.additional_vehicles && booking.additional_vehicles.map((av, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4">
                          <p className="font-medium text-slate-900">Additional Vehicle: {av.service_type}</p>
                          <p className="text-sm text-slate-500 mt-1">
                            {av.vehicle.year} {av.vehicle.make} {av.vehicle.model}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-right font-medium text-slate-900">
                          ${av.price?.toFixed(2) || '0.00'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 border-t border-slate-200">
                    <tr>
                      <td className="px-6 py-3 text-sm font-medium text-slate-500 text-right">Subtotal</td>
                      <td className="px-6 py-3 text-sm font-medium text-slate-900 text-right">${subtotal.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-3 text-sm font-medium text-slate-500 text-right">Tax (6.625%)</td>
                      <td className="px-6 py-3 text-sm font-medium text-slate-900 text-right">${tax.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-base font-bold text-slate-900 text-right">Total</td>
                      <td className="px-6 py-4 text-base font-bold text-emerald-600 text-right">${total.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-slate-200 pt-8 text-center text-sm text-slate-500">
              <p className="font-medium text-slate-900 mb-1">Thank you for choosing Platinum Touch!</p>
              <p>Lakewood, NJ • Orlando, FL</p>
              <p className="mt-2">platinumtouchnj@gmail.com</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
